/**
 * カレンダーボタン
 * Reusable Calendar Input Component
 */

import 'react-datepicker/dist/react-datepicker.css';

import { ja, Locale } from 'date-fns/locale';
import { forwardRef, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';

interface InputCalendarProps {
  onChange: (date: string | null, isUserInput?: boolean) => void;
  initialValue?: string;
  maxDate?: Date;
  minDate?: Date;
  placeholderText?: string;
  dateFormat?: string | string[];
  locale?: Locale;
  disabled?: boolean;
  className?: string;
  isClearable?: boolean;
  showTimeSelect?: boolean;
  timeFormat?: string;
  timeIntervals?: number;
  timeCaption?: string;
}

const InputCalendar = forwardRef<HTMLInputElement, InputCalendarProps>(
  (
    {
      onChange,
      initialValue,
      minDate = new Date('1970-01-01'),
      maxDate = new Date('2038-01-01'),
      placeholderText = 'YYYY-MM-DD',
      dateFormat = ['yyyy-MM-dd', 'yyyy/MM/dd', 'yyyyMMdd', 'ddMMyyyy', 'dd/MM/yyyy', 'dd-MM-yyyy'],
      locale = ja,
      disabled = false,
      className = 'w-full rounded border border-solid border-gray-200 bg-calendar bg-[center_right_12px] bg-no-repeat px-4 py-[10px] text-sm text-gray-700 outline-none',
      isClearable = false,
      showTimeSelect = false,
      timeFormat = 'HH:mm',
      timeIntervals = 15,
      timeCaption = '時間',
    },
    _ref, // eslint-disable-line @typescript-eslint/no-unused-vars
  ) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(
      initialValue ? new Date(initialValue) : null,
    );

    useEffect(() => {
      if (initialValue) {
        const date = new Date(initialValue);
        setSelectedDate(date);
        const formattedDate = showTimeSelect ? formatDateWithTime(date) : formatDateOnly(date);
        onChange(formattedDate, false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue]);

    const formatDateOnly = (date: Date): string => {
      return date.toLocaleDateString('en-CA');
    };

    const formatDateWithTime = (date: Date): string => {
      const dateStr = date.toLocaleDateString('en-CA');
      const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      return `${dateStr} ${timeStr}`;
    };

    const handleDateChange = (date: Date | null) => {
      setSelectedDate(date);

      if (date) {
        const formattedDate = showTimeSelect ? formatDateWithTime(date) : formatDateOnly(date);
        onChange(formattedDate, true);
      } else {
        onChange(null, true);
      }
    };

    return (
      <DatePicker
        className={className}
        placeholderText={placeholderText}
        locale={locale}
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat={dateFormat}
        maxDate={maxDate}
        minDate={minDate}
        disabled={disabled}
        isClearable={isClearable}
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        timeIntervals={timeIntervals}
        timeCaption={timeCaption}
      />
    );
  },
);

InputCalendar.displayName = 'InputCalendar';

export default InputCalendar;
